/**
 * Reusable popup custom element (ir-popup).
 * Types: 'info' (message + close), 'confirm' (message + cancel + confirm with callback).
 * Use theme modal.css for styling.
 *
 * Usage:
 *   const popup = document.querySelector('ir-popup');
 *   // Info (single close button)
 *   popup.show({ type: 'info', title: 'Notice', message: 'Something happened.' });
 *   // Confirm (cancel + confirm, onConfirm required)
 *   popup.show({
 *     type: 'confirm',
 *     title: 'Are you sure?',
 *     message: 'This action cannot be undone.',
 *     confirmLabel: 'Yes',
 *     cancelLabel: 'No',
 *     onConfirm: () => { ... },
 *     onCancel: () => { ... }  // optional
 *   }, openerElement);
 */

(function () {
  if (customElements.get('ir-popup')) return;

  const irPopupTrapHandlers = {};

  function removeTrapFocus(elementToFocus = null) {
    document.removeEventListener('focusin', irPopupTrapHandlers.focusin);
    document.removeEventListener('focusout', irPopupTrapHandlers.focusout);
    document.removeEventListener('keydown', irPopupTrapHandlers.keydown);
    if (elementToFocus) elementToFocus.focus();
  }

  function trapFocus(container, elementToFocus = container) {
    const focusableSelector = 'button:not([disabled]), [href], input:not([type="hidden"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex^="-"])';
    const focusableEls = Array.from(container.querySelectorAll(focusableSelector));
    const visible = focusableEls.filter((el) => el.offsetParent && getComputedStyle(el).visibility !== 'hidden');
    const firstEl = visible[0] ?? container;
    const lastEl = visible[visible.length - 1] ?? container;

    removeTrapFocus();

    irPopupTrapHandlers.keydown = (evt) => {
      if (evt.code !== 'Tab') return;
      if (evt.target === lastEl && !evt.shiftKey) {
        evt.preventDefault();
        firstEl.focus();
      }
      if ((evt.target === container || evt.target === firstEl) && evt.shiftKey) {
        evt.preventDefault();
        lastEl.focus();
      }
    };

    irPopupTrapHandlers.focusin = () => {
      document.addEventListener('keydown', irPopupTrapHandlers.keydown);
    };
    irPopupTrapHandlers.focusout = () => {
      document.removeEventListener('keydown', irPopupTrapHandlers.keydown);
    };

    document.addEventListener('focusout', irPopupTrapHandlers.focusout);
    document.addEventListener('focusin', irPopupTrapHandlers.focusin);
    (elementToFocus || container).focus();
  }

  const DEFAULT_LABELS = {
    close: window.theme.strings.irPopup.closeTitle || 'Close',
    cancel: window.theme.strings.irPopup.cancelTitle || 'Cancel',
    confirm: window.theme.strings.irPopup.confirmTitle || 'Confirm'
  };

class IrPopup extends HTMLElement {
  constructor() {
    super();
    this._openedBy = null;
    this._options = null;
    this._keyupHandler = null;
    this._confirmed = false;
    this.classList.add('modal', 'fixed', 'top-0', 'left-0', 'w-full', 'h-full', 'flex', 'items-center', 'justify-center', 'invisible');
    this.setAttribute('role', 'dialog');
    this.setAttribute('aria-modal', 'true');
    this.setAttribute('aria-hidden', 'true');
    this.addEventListener('click', this._handleClick.bind(this));
  }

  /**
   * Show the popup.
   * @param {Object} options
   * @param {'info'|'confirm'} options.type - Popup type.
   * @param {string} options.title - Heading text.
   * @param {string} options.message - Body text.
   * @param {string} [options.confirmLabel] - Confirm button label (confirm type).
   * @param {string} [options.cancelLabel] - Cancel button label (confirm type).
   * @param {string} [options.closeLabel] - Close button label (info type).
   * @param {Function} [options.onConfirm] - Callback when user confirms (confirm type).
   * @param {Function} [options.onCancel] - Callback when user cancels or closes (confirm type).
   * @param {Element} [opener] - Element that opened the popup (for focus return).
   */
  show(options, opener = null) {
    const type = options?.type ?? 'info';
    const title = options?.title ?? '';
    const message = options?.message ?? '';
    const labels = {
      close: options?.closeLabel ?? DEFAULT_LABELS.close,
      cancel: options?.cancelLabel ?? DEFAULT_LABELS.cancel,
      confirm: options?.confirmLabel ?? DEFAULT_LABELS.confirm
    };

    this._options = options;
    this._openedBy = opener ?? null;
    this._render(type, title, message, labels);
    this.setAttribute('open', '');
    this.setAttribute('aria-hidden', 'false');
    if (this._options?.ariaLabel) {
      this.setAttribute('aria-label', this._options.ariaLabel);
    } else {
      this.setAttribute('aria-labelledby', 'ir-popup-heading');
    }

    trapFocus(this);
    if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();

    this._keyupHandler = (evt) => {
      if (evt.key === 'Escape') this.close();
    };
    this.addEventListener('keyup', this._keyupHandler);
  }

  /**
   * Close the popup and run cancel callback if present (confirm type).
   */
  close() {
    if (!this.hasAttribute('open')) return;

    const wasConfirm = this._options?.type === 'confirm';
    const hadCancel = typeof this._options?.onCancel === 'function';
    const runCancel = wasConfirm && hadCancel && !this._confirmed;
    const openedBy = this._openedBy;

    this.removeEventListener('keyup', this._keyupHandler);
    this._keyupHandler = null;
    this._openedBy = null;

    removeTrapFocus(openedBy);

    this.removeAttribute('open');
    this.setAttribute('aria-hidden', 'true');
    if (typeof window.pauseAllMedia === 'function') window.pauseAllMedia();

    if (runCancel) this._options.onCancel();
    this._options = null;
    this._confirmed = false;
  }

  _handleClick(evt) {
    const isOverlay = evt.target === this;
    const isCloseTrigger = evt.target.closest('.js-close-modal') || evt.target.closest('[data-ir-popup-cancel]');
    if (!isOverlay && !isCloseTrigger) return;
    this.close();
  }

  _render(type, title, message, labels) {
    const closeBtn = `<button type="button" class="modal__close-btn absolute js-close-modal" aria-label="${labels.close}">
      <svg width="24" height="24" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5" fill="none" fill-rule="evenodd" stroke-linejoin="round" aria-hidden="true" focusable="false" role="presentation" class="icon"><path d="M5 19 19 5M5 5l14 14"/></svg>
    </button>`;

    let actions = '';
    if (type === 'info') {
      actions = `<div class="flex flex-wrap gap-3 mt-6">
        <button type="button" class="btn btn--primary js-close-modal">${labels.close}</button>
      </div>`;
    } else {
      actions = `<div class="flex flex-wrap gap-3 mt-6">
        <button type="button" class="btn btn--secondary" data-ir-popup-cancel>${labels.cancel}</button>
        <button type="button" class="btn btn--primary" data-ir-popup-confirm>${labels.confirm}</button>
      </div>`;
    }

    this.innerHTML = `
      <div class="modal__window relative bg-theme-bg text-theme-text text-start overflow-hidden has-motion" role="document">
        ${closeBtn}
        <div class="modal__content flex-auto">
          <h2 class="h5 mb-4" id="ir-popup-heading">${this._escapeHtml(title)}</h2>
          ${this._options?.html ? `<div class="mb-0">${message}</div>` : `<p class="mb-0">${this._escapeHtml(message)}</p>`}
          ${actions}
        </div>
      </div>`;

    const confirmBtn = this.querySelector('[data-ir-popup-confirm]');
    if (confirmBtn && type === 'confirm' && typeof this._options?.onConfirm === 'function') {
      confirmBtn.addEventListener('click', () => {
        this._confirmed = true;
        this._options.onConfirm();
        this.close();
      }, { once: true });
    }
  }

  _escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

  customElements.define('ir-popup', IrPopup);
})();
